// src/components/ChatWindow.jsx
import React, { useState, useEffect } from 'react';
import { Box, Paper, TextField, Avatar, Badge, Typography, useTheme } from '@mui/material';
import { MealMessage, OverMessage } from './BotMessage';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

function ChatWindow() {
  const commands = React.useMemo(() => ["/옵치 정보", "/급식 확인"], []);
  const [commandIndex, setCommandIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  useEffect(() => {
    if (!inView) return;

    const currentCommand = commands[commandIndex];
    let timeout;

    if (!isDeleting) {
      if (charIndex < currentCommand.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentCommand.substring(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        }, 150);
      } else {
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, 1000);
      }
    } else {
      if (charIndex > 0) {
        timeout = setTimeout(() => {
          setDisplayText(currentCommand.substring(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        }, 100);
      } else {
        timeout = setTimeout(() => {
          setIsDeleting(false);
          setCommandIndex((prev) => (prev + 1) % commands.length);
        }, 500);
      }
    }

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, commandIndex, commands, inView]);

  const outlineWidth = '7px';

  return (
    <Box
      ref={ref}
      sx={{
        height: { xs: '120vh', sm: '120vh', md: '100vh' },
        
        px: { xs: 2, sm: '10%', md: '20%' },
        py: { xs: 5, sm: 10, md: 7 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 6, md: 10 },
      }}
    >
      {/* 왼쪽 텍스트 영역 */}
      <motion.div
        initial={{ x: -50, opacity: 0 }}
        animate={inView ? { x: 0, opacity: 1 } : {}}
        transition={{ duration: 0.5 }}
      >
        <Box
          sx={{
            mx: { xs: 'auto', md: 0 },
            width: '600px',
            height: '100%', 
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              textAlign: { xs: 'center', md: 'right' },
              fontSize: { xs: '3rem', sm: '5rem', md: '7rem' },
              whiteSpace: { xs: 'normal', md: 'nowrap' },
              mt: { sm: -3, md: -5 },
              mb: { xs: -8, sm: -14, md: -2 },
              mr: { xs: 23, sm: 40, md: 13 },
              // color: isDarkMode ? '#36393f' : '#f2f3f6',
              color: isDarkMode ? '#36393f' : '#5e5d5d',
              WebkitTextStrokeWidth: outlineWidth,
              WebkitTextStrokeColor: theme.palette.mode === 'light' ? '#f2f3f6' : theme.palette.text.primary,
              paintOrder: 'stroke fill',
            }}
          >
            직관적인
          </Typography>

          <Typography
            sx={{
              fontWeight: 'bold',
              textAlign: { xs: 'center', md: 'left' },
              mb: { xs: -3, sm: -8, md: 1 },
              ml: { xs: 22, sm: 36, md: -8 },
              fontSize: { xs: '3rem', sm: '5rem', md: '7rem' },
              whiteSpace: { xs: 'normal', md: 'nowrap' },
              // color: isDarkMode ? '#36393f' : '#f2f3f6',
              color: isDarkMode ? '#36393f' : '#5e5d5d',
              WebkitTextStrokeWidth: outlineWidth,
              WebkitTextStrokeColor: theme.palette.mode === 'light' ? '#f2f3f6' : theme.palette.text.primary,
              paintOrder: 'stroke fill',
            }}
          >
            명령어로
          </Typography>

          <Typography
            sx={{
              fontWeight: 'bold',
              textAlign: { xs: 'center', md: 'right' },
              mt: { sm: 6, md: -2 },
              mb: { xs: -5, sm: -7, md: 0 },
              fontSize: { xs: '6rem', sm: '8rem', md: '12rem' },
              whiteSpace: { xs: 'normal', md: 'nowrap' },
              color: theme.palette.mode === 'light' ? '#7ab4f5' : theme.palette.text.primary,
            }}
          >
            편하게
          </Typography>
        </Box>
      </motion.div>

      {/* 오른쪽 디스코드 채팅 박스 */}
      <motion.div
        initial={{ x: 50, opacity: 0 }}
        animate={inView ? { x: 0, opacity: 1 } : {}}
        transition={{ duration: 0.5 }}
      >
        <Paper
          sx={{
            width: '100vh',
            mt: { xs: 0, sm: 0, md: 6 },
            position: 'relative',
            width: { xs: 350, sm: 400, md: 400 },
            height: { xs: '87vh', sm: '77vh', md: '77vh' },
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#2f3136',
            color: 'white',
            borderRadius: '15px',
            overflow: 'hidden',
            justifyContent: 'center',
          }}
        >
          {/* 헤더 */}
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid #202225',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#44b700',
                    boxShadow: `0 0 0 2px #2f3136`,
                  },
                }}
              >
                <Avatar alt="전적이" src="/profile.webp" sx={{ width: 40, height: 40 }} />
              </Badge>
              <Typography variant="h6" sx={{ ml: 2 }}>
                전적이
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ff5f57' }} />
              <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ffbd2e' }} />
              <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#28c840' }} />
            </Box>
          </Box>

          {/* 메시지 영역 */}
          <Box sx={{ flexGrow: 1, px: 2, pt: 2, pb: 0, overflowY: 'visible', }}>
            <MealMessage />
            <Box sx={{ height: '1px' }} />
            <OverMessage />
          </Box>

          {/* 타이핑 입력창 */}
          <Box sx={{ p: 2, borderTop: '1px solid #202225' }}>
            <TextField
              variant="outlined"
              fullWidth
              value={displayText}
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: '#40444b',
                  borderRadius: '5px',
                  color: 'white',
                  fontFamily: 'monospace',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                },
              }}
            />
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
}

export default ChatWindow;
